import {
  AddIcon,
  Button,
  DeleteIcon,
  Drawer,
  EditIcon,
  LoadingLoop,
  Tabs,
} from '@medeloop/medeloop-components-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Outlet,
  createSearchParams,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import {
  getDataSourceById,
  getDataSourceOrganizations,
  getDataSourceResearchers,
} from 'src/api/data-sources';
import { TooltipButton } from 'src/components';
import { DataSourceResearchersTable } from 'src/components/data-sources/tables/data-source-researchers-table';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { DataSource, DataSourceResearcher } from 'src/types/data-sources';
import { OrganizationDataSource } from 'src/types/organizations';
import { useFlags } from 'launchdarkly-react-client-sdk';

type OutletContextType = {
  container: HTMLElement;
};

export const DataSourcesDetailsPage = () => {
  const token = useSelector(selectAuthToken);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectAccount);
  const { t } = useTranslation();
  const { createCohortBuilder } = useFlags();

  const { dataSourceId } = useParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  if (!dataSourceId) {
    navigate(-1);
  }

  // set container for drawer children
  const [container, setContainer] = useState(document.body);
  const drawerRef = useRef(null);
  useEffect(() => {
    if (drawerRef.current) {
      setContainer(drawerRef.current);
    }
  }, [drawerRef]);

  // Fetch data
  const { isLoading, isError, data, refetch } = useQuery<
    | {
        dataSource: DataSource;
        organizations: OrganizationDataSource[];
      }
    | undefined
  >({
    queryKey: ['data-source', dataSourceId],
    queryFn: async () => {
      if (dataSourceId) {
        const [dataSource, organizations] = await Promise.all([
          getDataSourceById({ token, dataSourceId }),
          getDataSourceOrganizations({ token, dataSourceId }),
        ]);

        return { dataSource, organizations: organizations.organizations };
      }
    },
  });

  // check permissions
  const isAdmin =
    data &&
    data.organizations?.some((org: OrganizationDataSource) =>
      user.organizationAdminIds.some((id) => org.organizationId.startsWith(id)),
    );

  const dataSourceResearchersQuery = useQuery<{
    researchers: DataSourceResearcher[];
  }>({
    queryKey: ['data-source', dataSourceId, 'researchers'],
    queryFn: async () => {
      if (dataSourceId) {
        return getDataSourceResearchers({ token, dataSourceId });
      }
    },
  });

  // Refetch data when location changes
  useEffect(() => {
    refetch();
  }, [location]);

  if (isError) {
    navigate('/overview');
  }

  const handleEdit = () => {
    if (!data) return;
    navigate({
      pathname: 'update',
      search: `?${createSearchParams({
        dataSourceId: data.dataSource.dataSourceId,
        displayName: data.dataSource.displayName,
        name: data.dataSource.name,
        description: data.dataSource.description,
      })}`,
    });
  };
  const handleDelete = () => {
    if (!data) return;
    navigate({
      pathname: 'delete',
      search: `?${createSearchParams({
        dataSourceId: data?.dataSource.dataSourceId,
      })}`,
    });
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={() => {
        setIsDrawerOpen(false);
        navigate('/overview');
      }}
    >
      <Drawer.Overlay />
      <Drawer.Content className="w-5/6 overflow-auto" ref={drawerRef}>
        {isLoading && (
          <div className="flex flex-grow items-center justify-center">
            <LoadingLoop className="w-16" />
          </div>
        )}

        {data && (
          <div className="flex flex-col flex-grow gap-4">
            <Drawer.Header>
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <Drawer.Title>{data.dataSource.displayName}</Drawer.Title>
                  <Drawer.Description>{data.dataSource.name}</Drawer.Description>
                </div>

                {/* Actions */}
                <div className="flex flex-grow justify-end gap-2 items-start">
                  {createCohortBuilder && (
                    <Button onClick={() => navigate(`/dataset/${dataSourceId}/create-cohort`)}>
                      <AddIcon className="h-5 w-5" />
                      {t('create_cohort')}
                    </Button>
                  )}
                  <TooltipButton tooltipText={t('edit_data_source')} onClick={handleEdit}>
                    <EditIcon className="h-5 w-5" />
                  </TooltipButton>
                  <TooltipButton tooltipText={t('delete_data_source')} onClick={handleDelete}>
                    <DeleteIcon className="h-5 w-5" />
                  </TooltipButton>
                </div>
              </div>
              {data.organizations.length > 0 && data.organizations[0] && (
                <div className="flex pt-4">
                  <span className="py-2 px-3 border border-yellow-500 text-yellow-500 rounded-3xl text-sm font-semibold">
                    {data.organizations[0].organizationName}
                  </span>
                </div>
              )}
            </Drawer.Header>

            {/* General Information */}
            <div className="flex flex-col gap-4">
              <span className="text-white font-bold">{t('general_information')}</span>
              <div className="flex bg-gray-900 p-6 text-white rounded-lg gap-16">
                <div className="flex flex-col gap-2">
                  <span className="font-semibold">Id</span>
                  <span className="text-gray-500 text-sm">{data.dataSource.dataSourceId}</span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="font-semibold">{t('description')}</span>
                  <span className="text-gray-500 text-sm">
                    {data.dataSource.description || t('no_description_provided')}
                  </span>
                </div>
              </div>
            </div>

            {/* Overview */}
            <div className="flex flex-grow flex-col gap-4">
              <span className="text-white font-bold text-lg">{t('users')}</span>

              <Tabs defaultValue="researchers" className="bg-gray-900 text-white rounded-lg">
                <Tabs.List>
                  <Tabs.Trigger value="researchers">{t('researchers_header')}</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="researchers">
                  <DataSourceResearchersTable
                    researchers={dataSourceResearchersQuery?.data?.researchers || []}
                    allowActions={isAdmin}
                  />
                </Tabs.Content>
              </Tabs>
            </div>
          </div>
        )}
        <Outlet context={{ container }} />
      </Drawer.Content>
    </Drawer>
  );
};

export const useDataSourceDetailsContext = () => {
  return useOutletContext<OutletContextType>();
};
